import { LicenseCoarseLevels } from '../hooks/useAccessBadge';

/**
 * Levers for who gets the Intercom chat widget. An org qualifies if it
 * matches ANY of these — country, region, or plan — so change a list here
 * rather than touching `shouldShowIntercomChat` below.
 *
 * Default rule: show Intercom for orgs in the US, or for any org that isn't
 * on Freemium (a paid Singapore teacher still sees it — plan alone qualifies
 * them even though country/region don't).
 */
export const INTERCOM_VISIBILITY_CONFIG = {
  // `organization.country` values (ISO-ish codes as stored on the org, e.g.
  // 'US') that always get Intercom, regardless of plan.
  countries: ['US'] as string[],
  // `organization.region` values (e.g. a US state, or any other org-level
  // region string) that always get Intercom, regardless of plan. Empty by
  // default — add entries for regions that should qualify even when their
  // country isn't in `countries`.
  regions: [] as string[],
  // License tiers that always get Intercom, regardless of country/region.
  // Freemium is deliberately excluded — that's the tier this rule gates.
  plans: [
    LicenseCoarseLevels.premium,
    LicenseCoarseLevels.enterprise,
  ] as LicenseCoarseLevels[],
};

export type IntercomVisibilityInput = {
  country?: string | null;
  region?: string | null;
  licenseCoarseLevel?: LicenseCoarseLevels | null;
};

/**
 * Whether the Intercom chat widget should be shown to a logged-in user, based
 * on their active organization's country/region and license tier.
 *
 * Each lever is an OR, not an AND: a US org sees it on Freemium too, and a
 * non-US, non-listed-region org sees it as soon as it's paid.
 */
export function shouldShowIntercomChat({
  country,
  region,
  licenseCoarseLevel,
}: IntercomVisibilityInput): boolean {
  if (country && INTERCOM_VISIBILITY_CONFIG.countries.includes(country)) {
    return true;
  }
  if (region && INTERCOM_VISIBILITY_CONFIG.regions.includes(region)) {
    return true;
  }
  if (
    licenseCoarseLevel &&
    INTERCOM_VISIBILITY_CONFIG.plans.includes(licenseCoarseLevel)
  ) {
    return true;
  }
  return false;
}
